/**
 * NDA Signing Gate — blocks access to the entire application until the user
 * signs the current NDA version. Presents a full-screen modal with:
 * - Scrollable NDA text
 * - Type-to-sign (full legal name)
 * - Draw-to-sign (canvas for finger/mouse/stylus signature)
 * - Consent checkboxes (electronic consent + terms agreement)
 * - Consumer disclosure text (ESIGN Act compliance)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { ndaApi } from "@/api/services";
import type { NDADocument, NDAStatus } from "@/api/types";
import { Loader2, FileText, PenTool, Type, RotateCcw, Download, ChevronDown } from "lucide-react";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";

// ---------------------------------------------------------------------------
// Signature Canvas Component (draw-to-sign)
// ---------------------------------------------------------------------------

function SignatureCanvas({
  onSignatureChange,
  disabled,
}: {
  onSignatureChange: (dataUrl: string | null) => void;
  disabled: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const hasDrawn = useRef(false);

  const getPos = useCallback(
    (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      if ("touches" in e) {
        const touch = e.touches[0];
        if (!touch) return null;
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
      }
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    []
  );

  const startDraw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;
      e.preventDefault();
      isDrawing.current = true;
      const pos = getPos(e);
      if (!pos) return;
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    },
    [disabled, getPos]
  );

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing.current || disabled) return;
      e.preventDefault();
      const pos = getPos(e);
      if (!pos) return;
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1a1a2e";
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      hasDrawn.current = true;
    },
    [disabled, getPos]
  );

  const endDraw = useCallback(() => {
    isDrawing.current = false;
    if (hasDrawn.current && canvasRef.current) {
      onSignatureChange(canvasRef.current.toDataURL("image/png"));
    }
  }, [onSignatureChange]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    onSignatureChange(null);
  }, [onSignatureChange]);

  return (
    <div className="space-y-2">
      <div className="relative rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasDrawn.current && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
            Draw your signature here
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        disabled={disabled}
        className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
      >
        <RotateCcw className="h-3 w-3" /> Clear signature
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main NDA Signing Gate
// ---------------------------------------------------------------------------

export function NDASigningGate({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<NDAStatus | null>(null);
  const [document, setDocument] = useState<NDADocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");
  const [presentedAt] = useState(() => new Date().toISOString());

  // Form state
  const [fullName, setFullName] = useState("");
  const [signatureMode, setSignatureMode] = useState<"TYPED" | "DRAWN" | "BOTH">("TYPED");
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [consentElectronic, setConsentElectronic] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Blocking gate: trap focus + lock scroll while the NDA modal is shown.
  // Intentionally non-dismissable (Escape must not bypass the gate).
  const gateOpen = !loading && !!status?.nda_required && !status.user_signed;
  const dialogRef = useModalA11y<HTMLDivElement>({
    open: gateOpen,
    onClose: () => {},
    dismissable: false,
  });

  // Check NDA status on mount
  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const s = await ndaApi.getStatus();
        if (cancelled) return;
        setStatus(s);
        if (s.nda_required && !s.user_signed) {
          const doc = await ndaApi.getDocument();
          if (!cancelled) setDocument(doc);
        }
      } catch {
        // If NDA check fails, let user through (fail-open for usability)
        if (!cancelled) setStatus({ nda_required: false, current_version: null, document_id: null, user_signed: false, signed_at: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  // Track scroll position
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Sign the NDA
  const handleSign = async () => {
    setError("");

    if (!fullName.trim() || fullName.trim().length < 2) {
      setError("Please enter your full legal name");
      return;
    }
    if (signatureMode !== "TYPED" && !signatureDataUrl) {
      setError("Please draw your signature");
      return;
    }
    if (!consentElectronic) {
      setError("You must consent to electronic signing");
      return;
    }
    if (!consentTerms) {
      setError("You must agree to the NDA terms");
      return;
    }

    setSigning(true);
    try {
      await ndaApi.sign({
        signer_full_name: fullName.trim(),
        signature_type: signatureMode,
        signature_data_url: signatureDataUrl ?? undefined,
        consent_to_electronic: consentElectronic,
        consent_to_terms: consentTerms,
        nda_presented_at: presentedAt,
        user_agent: navigator.userAgent,
      });
      // Refresh status
      setStatus((prev) => prev ? { ...prev, user_signed: true, signed_at: new Date().toISOString() } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign NDA. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // No NDA required or already signed — render children
  if (!status?.nda_required || status.user_signed) {
    return <>{children}</>;
  }

  // NDA signing gate — full-screen modal
  const canSign = fullName.trim().length >= 2 && consentElectronic && consentTerms &&
    (signatureMode === "TYPED" || signatureDataUrl);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
      <div
        ref={dialogRef}
        {...dialogA11yProps({ labelledById: "nda-gate-title", describedById: "nda-gate-desc" })}
        className="mx-4 flex max-h-[95vh] w-full max-w-3xl flex-col rounded-2xl bg-white dark:bg-slate-900 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/50">
            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 id="nda-gate-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {document?.title || "Non-Disclosure Agreement"}
            </h1>
            <p id="nda-gate-desc" className="text-sm text-slate-600 dark:text-slate-300">
              Version {document?.version || "1.0"} — Please read and sign to continue
            </p>
          </div>
        </div>

        {/* Scrollable NDA content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-4"
          style={{ maxHeight: "40vh" }}
        >
          <div className="prose prose-sm dark:prose-invert max-w-none">
            {document?.content.split("\n").map((line, i) => {
              if (!line.trim()) return <br key={i} />;
              if (/^\d+\./.test(line.trim())) {
                return <h3 key={i} className="mt-4 mb-2 text-sm font-bold text-slate-900 dark:text-slate-100">{line}</h3>;
              }
              if (line.startsWith("NON-DISCLOSURE AGREEMENT")) {
                return <h2 key={i} className="mb-4 text-center text-base font-bold text-slate-900 dark:text-slate-100">{line}</h2>;
              }
              if (line.startsWith("WHEREAS") || line.startsWith("NOW, THEREFORE")) {
                return <p key={i} className="mb-2 italic text-slate-600 dark:text-slate-300">{line}</p>;
              }
              if (/^\([a-z]\)/.test(line.trim())) {
                return <p key={i} className="ml-6 mb-1 text-slate-600 dark:text-slate-300">{line}</p>;
              }
              if (line.startsWith("CONSUMER DISCLOSURE:")) {
                return <p key={i} className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-200">{line}</p>;
              }
              return <p key={i} className="mb-2 text-slate-600 dark:text-slate-300">{line}</p>;
            })}
          </div>

          {!hasScrolledToBottom && (
            <div className="sticky bottom-0 flex justify-center pb-2">
              <span className="flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/50 px-3 py-1 text-xs font-medium text-blue-700 dark:text-blue-300">
                <ChevronDown className="h-3 w-3 animate-bounce" /> Scroll to read the full agreement
              </span>
            </div>
          )}
        </div>

        {/* Signing section */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 space-y-4">
          {/* Signature mode selector */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSignatureMode("TYPED")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                signatureMode === "TYPED"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                  : "bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <Type className="h-3.5 w-3.5" /> Type name
            </button>
            <button
              type="button"
              onClick={() => setSignatureMode("DRAWN")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                signatureMode === "DRAWN"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                  : "bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <PenTool className="h-3.5 w-3.5" /> Draw signature
            </button>
            <button
              type="button"
              onClick={() => setSignatureMode("BOTH")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                signatureMode === "BOTH"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                  : "bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              Both
            </button>
          </div>

          {/* Full legal name input */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Full Legal Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full legal name"
              disabled={signing}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {signatureMode === "TYPED" && fullName.trim().length >= 2 && (
              <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3">
                <p className="text-xs text-slate-600 dark:text-slate-300 mb-1">Signature preview:</p>
                <p
                  className="text-2xl text-slate-900 dark:text-slate-100"
                  style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Dancing Script', cursive" }}
                >
                  {fullName.trim()}
                </p>
              </div>
            )}
          </div>

          {/* Drawn signature canvas */}
          {(signatureMode === "DRAWN" || signatureMode === "BOTH") && (
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Draw Your Signature <span className="text-red-500">*</span>
              </label>
              <SignatureCanvas
                onSignatureChange={setSignatureDataUrl}
                disabled={signing}
              />
            </div>
          )}

          {/* Consent checkboxes */}
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={consentElectronic}
                onChange={(e) => setConsentElectronic(e.target.checked)}
                disabled={signing}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-slate-600 dark:text-slate-300">
                I consent to sign this agreement electronically. I understand I have the right
                to receive this document in paper form and may withdraw electronic consent by
                contacting support@ftldevelopment.com.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={consentTerms}
                onChange={(e) => setConsentTerms(e.target.checked)}
                disabled={signing}
                className="mt-0.5 h-4 w-4 rounded border-slate-200 dark:border-slate-700 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-slate-600 dark:text-slate-300">
                I have read, understand, and agree to all terms and conditions of this
                Non-Disclosure Agreement. <span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          {/* Error */}
          {error && (
            <p className="rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => {
                if (document) {
                  const blob = new Blob([document.content], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = window.document.createElement("a");
                  a.href = url;
                  a.download = `NDA-v${document.version}.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }}
              className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
            >
              <Download className="h-3.5 w-3.5" /> Download copy
            </button>

            <button
              type="button"
              onClick={handleSign}
              disabled={!canSign || signing}
              className={`rounded-lg px-6 py-2.5 text-sm font-semibold shadow-sm transition-all ${
                canSign && !signing
                  ? "bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed"
              }`}
            >
              {signing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing...
                </span>
              ) : (
                "Sign NDA & Continue"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
